import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { escapeRegExp } from 'lodash';

import { Text, TextProps } from 'components/Layout';

import styles from './TextHighlighter.module.scss';

export interface TextHighlighterProps extends TextProps {
    /** Defines the text upon which the highlighter should operate */
    text?: string;
    /** Defines the text which should be highlighted */
    highlight?: string;
    /** Accepts additional style object for the highlighted text */
    highlightStyle?: React.CSSProperties;
    /** Accepts additional className for the highlighted text */
    highlightClassName?: string;
}

export const TextHighlighter: React.FC<TextHighlighterProps> = ({
    tag = null,
    text = '',
    highlight = '',
    highlightStyle,
    highlightClassName,
    ...rest
}) => {
    if (!text) return null;
    if (!highlight || typeof text !== 'string') {
        return (
            <Text always {...rest} tag={tag}>
                {text}
            </Text>
        );
    }

    let match;
    let lastIndex = 0;
    const parts = [];
    const cleanHighlight = escapeRegExp(highlight.toLowerCase());
    const regex = new RegExp(cleanHighlight.replace(/(\\?\S)/g, '$1\\s*?'), 'gi');

    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(text)) !== null) {
        const [string] = match;
        const start = match.index;

        parts.push(
            <span key={`${lastIndex}-text`} className={cx(styles.text)}>
                {text.slice(lastIndex, start)}
            </span>,
            <span
                key={`${lastIndex}-highlight`}
                style={highlightStyle}
                className={cx(styles.highlight, highlightClassName)}
            >
                {string}
            </span>,
        );

        lastIndex = match.index + string.length;
    }
    parts.push(
        <span key={`${lastIndex}-text`} className={cx(styles.text)}>
            {text.slice(lastIndex)}
        </span>,
    );

    return (
        <Text always {...rest} tag={tag}>
            {parts.filter(Boolean)}
        </Text>
    );
};

TextHighlighter.displayName = 'TextHighlighter';
TextHighlighter.propTypes = {
    tag: PropTypes.elementType as React.Validator<React.ElementType>,
    text: PropTypes.string,
    highlight: PropTypes.string,
    highlightStyle: PropTypes.shape({}),
    highlightClassName: PropTypes.string,
};
